import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import type { RosterTemplateLibraryEntry } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import type { PlanLibraryPlan } from 'hooks/planLibrary/planLibraryHooks';
import { emptyPlanLibraryTask } from './planLibraryTaskModel';
import { USCG_FOC_AOR_IDS } from '../AorsTab/utils/uscgFocAorCatalog';

/**
 * Demonstration plan and IMT templates shown while USCG FOC Requirements is on,
 * so the planning-repository tour has a repository record and two contingencies
 * even when the organization has saved none. They are appended to the live
 * lists and are never written back.
 */

export const USCG_FOC_PLAN_ID = 'f0c50400-0000-4000-a000-000000000001';

export const USCG_FOC_HURRICANE_TEMPLATE_ID =
  'f0c50500-0000-4000-a000-000000000001';
export const USCG_FOC_MER_TEMPLATE_ID = 'f0c50500-0000-4000-a000-000000000002';

export const USCG_FOC_HURRICANE_SLUG = 'uscg-foc-hurricane-imt';
export const USCG_FOC_MER_SLUG = 'uscg-foc-mer-imt';

const DEMO_AT = '2026-10-01T13:00:00.000Z';

const TEMPLATE_IDS = new Set([
  USCG_FOC_HURRICANE_TEMPLATE_ID,
  USCG_FOC_MER_TEMPLATE_ID,
]);

const TEMPLATE_SLUGS = new Set([USCG_FOC_HURRICANE_SLUG, USCG_FOC_MER_SLUG]);

export const isUscgFocPlanId = (id: string | null | undefined): boolean =>
  id === USCG_FOC_PLAN_ID;

export const isUscgFocRosterTemplateId = (
  id: string | null | undefined
): boolean => Boolean(id) && TEMPLATE_IDS.has(String(id));

export const isUscgFocRosterTemplateSlug = (
  slug: string | null | undefined
): boolean => Boolean(slug) && TEMPLATE_SLUGS.has(String(slug));

export const USCG_FOC_DEMO_APPLY_MESSAGE =
  'This demonstration item is not written into the workspace.';

const task = (id: string, name: string, description: string) => ({
  ...emptyPlanLibraryTask(),
  id,
  name,
  description,
});

const SEATS: RosterTemplateNodeDto[] = [
  { name: 'Incident Commander', parentName: null, category: 'Command' },
  {
    name: 'Operations Section Chief',
    parentName: 'Incident Commander',
    category: 'Operations',
  },
  {
    name: 'Planning Section Chief',
    parentName: 'Incident Commander',
    category: 'Planning',
  },
];

export function getUscgFocPlan(groupGuid: string): PlanLibraryPlan {
  return {
    id: USCG_FOC_PLAN_ID,
    groupGuid,
    name: 'Hurricane Response — Sector New York',
    description:
      'Approved demonstration plan for Sector New York hurricane response. Applying it copies these tasks into a workspace and leaves this plan unchanged.',
    tasks: [
      task(
        'f0c50410-0000-4000-a000-000000000001',
        'Establish the incident command post',
        'Stand up the Sector New York command post and confirm who is filling command and general staff.'
      ),
      task(
        'f0c50410-0000-4000-a000-000000000002',
        'Confirm port status and waterway closures',
        'Check harbor, channel, and ferry status before the operational period starts.'
      ),
    ],
    forms: [],
    files: [],
    rosterTemplateIds: [USCG_FOC_HURRICANE_TEMPLATE_ID],
    layers: [],
    useForIncidents: true,
    incidentCategories: ['Flood Hurricane/Typhoon'],
    aorIds: [USCG_FOC_AOR_IDS.sectorNewYork],
    responsiblePartyCompany: null,
    filledRosterTemplateId: null,
    templateIncidentId: null,
    archived: false,
    createdBy: null,
    createdAt: DEMO_AT,
    updatedAt: DEMO_AT,
  };
}

type TemplateSeed = {
  id: string;
  slug: string;
  name: string;
  description: string;
};

const TEMPLATE_SEEDS: TemplateSeed[] = [
  {
    id: USCG_FOC_HURRICANE_TEMPLATE_ID,
    slug: USCG_FOC_HURRICANE_SLUG,
    name: 'Hurricane Response IMT',
    description:
      'Demonstration template for Sector New York. Intended use: hurricane response. Unstaffed IMT the unit can load into a workspace without changing this template.',
  },
  {
    id: USCG_FOC_MER_TEMPLATE_ID,
    slug: USCG_FOC_MER_SLUG,
    name: 'Marine Environmental Response IMT',
    description:
      'Demonstration template for Sector New York. Intended use: marine environmental response. Kept separate from the hurricane template so one contingency does not overwrite the other.',
  },
];

export function getUscgFocRosterTemplates(
  groupGuid: string
): RosterTemplateLibraryEntry[] {
  return TEMPLATE_SEEDS.map((seed) => ({
    id: seed.id,
    groupGuid,
    name: seed.name,
    description: seed.description,
    attachOnly: false,
    nodes: SEATS,
    archived: false,
    createdBy: null,
    createdAt: DEMO_AT,
    updatedAt: DEMO_AT,
  }));
}

/** Catalog rows for the workspace Templates sidebar, under Organization. */
export function getUscgFocRosterCatalogEntries() {
  return TEMPLATE_SEEDS.map((seed) => ({
    slug: seed.slug,
    name: seed.name,
    description: seed.description,
    category: 'organization',
    attachOnly: false,
    positionCount: SEATS.length,
    nodes: SEATS,
  }));
}
